import isNil from 'lodash/isNil'
import keyBy from 'lodash/keyBy'
import partition from 'lodash/partition'
import { useMemo } from 'react'
import { useSystemCoreData, useSystemCoreDataSuspense } from '@omnea-app/fe-core-data/useSystemCoreData'
import { CurrencyCode, PREFERRED_CURRENCIES } from '../getCurrencyCodes'

export type CoreDataCurrency = { id: string; code: CurrencyCode; fxRate: number }

/**
 *
 * @returns All currencies that an organisation has access to.
 * This consists of the default currency and all currencies that have fx rates set for the organisation.
 * These currencies are returned with prioritised currencies at the top of the list
 */
export const useOrganisationCurrencies = () => {
  const {
    data: coreDataCurrencies,
    isLoading: isCoreDataCurrenciesLoading,
    error: coreDataCurrenciesError,
  } = useSystemCoreData({ coreDataType: 'currency' })

  return useMemo(() => {
    if (isCoreDataCurrenciesLoading || coreDataCurrenciesError || isNil(coreDataCurrencies)) {
      return { organisationCurrencies: [], loading: isCoreDataCurrenciesLoading, error: coreDataCurrenciesError }
    }

    const orderedCurrencies = coreDataCurrencies.slice().sort((a, b) => a.code.localeCompare(b.code))

    const [preferred, others] = partition(orderedCurrencies, (currency) => PREFERRED_CURRENCIES.includes(currency.code))

    const organisationCurrencies = [...preferred, ...others]
    const organisationCurrenciesByCode = keyBy(organisationCurrencies, 'code')

    return {
      organisationCurrencies,
      organisationCurrenciesByCode,
      loading: isCoreDataCurrenciesLoading,
      error: coreDataCurrenciesError,
    }
  }, [coreDataCurrencies, isCoreDataCurrenciesLoading, coreDataCurrenciesError])
}

export const useOrganisationCurrenciesSuspense = () => {
  const coreDataCurrencies = useSystemCoreDataSuspense({ coreDataType: 'currency' })

  const orderedCurrencies = coreDataCurrencies.sort((a, b) => a.code.localeCompare(b.code))

  return [
    ...orderedCurrencies.filter((currency) => PREFERRED_CURRENCIES.includes(currency.code)),
    ...orderedCurrencies.filter((currency) => !PREFERRED_CURRENCIES.includes(currency.code)),
  ]
}
